import { useQuery } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { SchemaDraftV1Service } from '../service';
import { GenerateSmartDraftIO } from '../types';

export const getGenerateSmartDraftQueryKey = (request: GenerateSmartDraftIO['input']) => {
  const baseKey = ['DraftV1', 'GenerateSmartDraft'] as const;
  const messagesKey =
    request.messages
      ?.map((msg) => `${msg.id}-${msg.locationId}`)
      .sort()
      .join('|') || '';

  return [...baseKey, messagesKey] as const;
};

type UseGenerateSmartDraftQueryArgs<
  E = unknown,
  D = GenerateSmartDraftIO['output'],
  RequestOverride extends GenerateSmartDraftIO['input'] = GenerateSmartDraftIO['input']
> = {
  request: RequestOverride;
  options?: LimitedSchemaQueryOptions<GenerateSmartDraftIO, E, D, ReturnType<typeof getGenerateSmartDraftQueryKey>>;
  httpOptions?: Options;
};

/**
 * A hook that returns a query for the `GenerateSmartDraft` endpoint.
 * Uses React Query's automatic request cancellation.
 *
 * @param request - The request object to pass to the query
 * @param options - Standard query options
 * @param httpOptions - HTTP request options
 */
export const useGenerateSmartDraftQuery = <
  E = unknown,
  D = GenerateSmartDraftIO['output'],
  RequestOverride extends GenerateSmartDraftIO['input'] = GenerateSmartDraftIO['input']
>({
  request,
  options,
  httpOptions,
}: UseGenerateSmartDraftQueryArgs<E, D, RequestOverride>) => {
  return useQuery<GenerateSmartDraftIO['output'], E, D, ReturnType<typeof getGenerateSmartDraftQueryKey>>({
    queryKey: getGenerateSmartDraftQueryKey(request),
    queryFn: (queryFunctionContext) =>
      SchemaDraftV1Service.GenerateSmartDraft(request, {
        ...httpOptions,
        signal: queryFunctionContext.signal,
      }),
    ...options,
  });
};
